'use client'

/**
 * StockProfitCalculator: free profit/loss tool for "stock profit calculator / how much did I make".
 * Buy price, sell price, shares and optional per-trade fees → gross and net profit, total return %,
 * and the breakeven sell price. Pure client math, no data dependency.
 */

import { useMemo, useState } from 'react'
import CalcField from '@/components/calc/CalcField'

const fmtUSD = (n: number): string => (isFinite(n) ? n.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 2 }) : '$0.00')
const fmtPct = (n: number): string => `${isFinite(n) && n >= 0 ? '+' : ''}${isFinite(n) ? n.toFixed(2) : '0.00'}%`

export default function StockProfitCalculator() {
  const [buy, setBuy] = useState('10.00')
  const [sell, setSell] = useState('12.50')
  const [shares, setShares] = useState('100')
  const [fees, setFees] = useState('0')

  const r = useMemo(() => {
    const b = parseFloat(buy) || 0
    const s = parseFloat(sell) || 0
    const q = parseFloat(shares) || 0
    const f = parseFloat(fees) || 0
    const cost = b * q + f // total fees split across the round trip, applied to cost basis
    const proceeds = s * q - f
    const pnl = proceeds - cost
    const pnlPct = cost > 0 ? (pnl / cost) * 100 : 0
    const breakeven = q > 0 ? (cost + f) / q : 0 // sell price needed to cover cost + exit fee
    const invalid = q <= 0 || b <= 0
    return { cost, proceeds, pnl, pnlPct, breakeven, invalid }
  }, [buy, sell, shares, fees])

  const win = r.pnl >= 0
  const accent = win ? '#34d399' : '#f43f5e'

  return (
    <div className="rounded-2xl border border-zinc-800 bg-zinc-900/40 p-4 sm:p-6">
      <div className="grid gap-4 sm:grid-cols-2">
        <CalcField label="Buy price" value={buy} onChange={setBuy} prefix="$" hint="What you paid per share" />
        <CalcField label="Sell price" value={sell} onChange={setSell} prefix="$" hint="What you sold (or plan to sell) for" />
        <CalcField label="Number of shares" value={shares} onChange={setShares} step="1" hint="Position size" />
        <CalcField label="Total fees / commissions" value={fees} onChange={setFees} prefix="$" hint="Optional, both sides combined" />
      </div>

      {r.invalid ? (
        <div className="mt-5 rounded-xl border border-zinc-700/70 bg-zinc-900/60 px-4 py-6 text-center text-sm text-zinc-400">
          Enter a buy price and number of shares to see the profit.
        </div>
      ) : (
        <>
          <div className="mt-5 grid gap-3 sm:grid-cols-2">
            <div className="rounded-2xl border px-5 py-4" style={{ borderColor: `${accent}4d`, background: `${accent}12` }}>
              <div className="text-[12px] font-bold uppercase tracking-wider" style={{ color: accent }}>{win ? 'Profit' : 'Loss'}</div>
              <div className="mt-1 text-[34px] font-black leading-none tabular-nums text-white">{fmtUSD(r.pnl)}</div>
              <div className="mt-1.5 text-[15px] font-bold tabular-nums" style={{ color: accent }}>{fmtPct(r.pnlPct)} return</div>
            </div>
            <div className="rounded-2xl border border-zinc-700/70 bg-zinc-900/60 px-5 py-4">
              <div className="text-[12px] font-bold uppercase tracking-wider text-zinc-400">Totals</div>
              <div className="mt-1.5 space-y-1 text-[14px] tabular-nums text-zinc-300">
                <div className="flex justify-between"><span className="text-zinc-500">Cost basis</span><span className="font-bold">{fmtUSD(r.cost)}</span></div>
                <div className="flex justify-between"><span className="text-zinc-500">Proceeds</span><span className="font-bold">{fmtUSD(r.proceeds)}</span></div>
                <div className="flex justify-between"><span className="text-zinc-500">Breakeven sell</span><span className="font-bold">{fmtUSD(r.breakeven)}</span></div>
              </div>
            </div>
          </div>
          <p className="mt-3 text-[11px] leading-relaxed text-zinc-600">
            Estimates only; fees are applied to both the cost basis and proceeds. Not tax or investment advice.
          </p>
        </>
      )}
    </div>
  )
}
